import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "./Header";
import Spinner from "../../../Components/common/Spinner/Spinner";
import Field from "../../Settings/Content/Components/Field";
import Backdrop from "../../../Components/UI/Backdrop/Backdrop";

import {
  RequestGetAlertSettings,
  RequestSetAlertSettings,
} from "../../../store/actions/mailGatewayActions";
import { addNotification } from "../../../store/actions/notifierActions";

const isValidServer = (input) => {
  const ipv4Regex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

  const hostnameRegex = /^(?=.{1,253}$)(?!\-)([a-zA-Z0-9\-]{1,63}(?<!\-)\.)+[a-zA-Z]{2,}$/;

  const trimmed = input.trim();

  return ipv4Regex.test(trimmed) || hostnameRegex.test(trimmed);
};

const isValidPort = (input) => {
  if (!input) return false;
  const trimmed = input?.trim?.() || input;
  const port = Number(trimmed);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

const AlertSettingsForm = ({ selectedProduct, selectedHost }) => {
  const dispatch = useDispatch();

  const [settings, setSettings] = useState({
    status: "",
    connection_type: "",
    server: "",
    port: "",
  });

  const settingsFromDB = useSelector((state) => state.mailGateway.alertSettings);
  const getSettingsLoading = useSelector((state) => state.mailGateway.alertSettingsLoading);
  const setSettingsLoading = useSelector((state) => state.mailGateway.setAlertSettingsLoading);

  const settingsStatus = settingsFromDB.status === "1";

  useEffect(() => {
    dispatch(RequestGetAlertSettings(selectedHost));
  }, [dispatch, selectedHost]);

  useEffect(() => {
    if (settingsFromDB && selectedHost) {
      setSettings({
        status: settingsFromDB?.status || "",
        connection_type: settingsFromDB?.connection_type || "",
        server: settingsFromDB?.server || "",
        port: Number(settingsFromDB?.port) || "",
      });
    }
  }, [settingsFromDB, selectedHost]);

  const toggleAlertSettingsHandler = () => {
    if (getSettingsLoading || setSettingsLoading) return;
    dispatch(
      RequestSetAlertSettings({
        host: selectedHost,
        reqBody: { status: settingsStatus ? 0 : 1 },
      }),
    );
  };

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (name === "server") {
      value = value.trim();
    } else if (name === "port" && value.length > 0) {
      value = value.trim();
      value = Number(value);
    }

    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const checkSaveButtonDisabled = () => {
    const keys = Object.keys(settings);
    for (const key of keys) {
      if (key === "port" && settings[key] === Number(settingsFromDB[key])) continue;
      else if (settings[key] === settingsFromDB[key]) continue;
      else if (settings[key] !== settingsFromDB[key]) return false;
    }
    return true;
  };

  const saveHandler = () => {
    if (getSettingsLoading || setSettingsLoading) return;
    if (!isValidServer(settings.server)) {
      dispatch(
        addNotification({
          message: "Enter a valid server",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    } else if (!isValidPort(settings.port)) {
      dispatch(
        addNotification({
          message: "Enter a valid port",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    } else {
      dispatch(RequestSetAlertSettings({ host: selectedHost, reqBody: settings }));
    }
  };

  return (
    <>
      <Header
        selectedProduct={selectedProduct}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "Status",
          active: settingsStatus,
          onClick: toggleAlertSettingsHandler,
        }}
      />
      <form className="products-body-form" onSubmit={(e) => e.preventDefault()}>
        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-20">
            <p className="light m-b-10 mailing__content__security__header">Connection Type</p>
            <div className="mailing__content__security__labels">
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="TCP"
                  name="connection_type"
                  checked={settings.connection_type === "TCP"}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">TCP</p>
              </label>
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="UDP"
                  name="connection_type"
                  checked={settings.connection_type === "UDP"}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">UDP</p>
              </label>
            </div>
          </div>
        </div>

        <div className="products-body-form__line">
          <div className="p-r-20">
            <div className="mailing__content__security m-b-20">
              <Field
                tooltipText="• Must be a valid IPv4 address (e.g., 192.168.1.1) or a domain name (e.g., mail.example.com).
• IPv4 segments must be between 0 and 255.
• Hostnames must not start or end with a hyphen (-) and can contain letters, digits, and hyphens.
• Maximum length: 253 characters."
                label="Server"
                width="162px"
                name="server"
                value={settings.server}
                onChange={inputChangeHandler}
              />
            </div>
          </div>
          <div className="mailing__content__security m-b-20">
            <Field
              tooltipText="• Must be a number between 1 and 65535.
• No letters, symbols, or spaces allowed.
• Leading and trailing spaces will be ignored."
              label="Port"
              width="70px"
              name="port"
              value={settings.port}
              onChange={inputChangeHandler}
            />
          </div>
        </div>

        <button
          type="button"
          className="save-button primary-button"
          onClick={saveHandler}
          disabled={checkSaveButtonDisabled()}>
          {getSettingsLoading || setSettingsLoading ? (
            <Spinner width={12} height={12} />
          ) : (
            "Save Settings"
          )}
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!settingsStatus}
          message="Alert settings are inactive! Please click on the status button at the header to activate alert settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default AlertSettingsForm;
